import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  Radio,
  CheckCircle2,
  AlertCircle,
  XCircle,
  PauseCircle,
  Star,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { useAuthStore } from '@/stores/authStore';
import {
  getChannelProviders,
  listChannelLines,
  createChannelLine,
  rotateChannelLine,
  disableChannelLine,
  disconnectChannelLine,
  setDefaultChannelLine,
  requirementsFor,
  startEmbeddedSignup,
  completeEmbeddedSignup,
  listChannelCapabilities,
  channelErrorCode,
  statusReasonCode,
  type ChannelProvider,
  type ChannelLine,
  type ChannelType,
  type LineStatus,
  type ChannelAccountCapability,
} from '@/api/channels';
import { launchEmbeddedSignup, EmbeddedSignupCancelled } from '@/lib/metaEmbeddedSignup';

/**
 * Ticket 138.1 — admin Web UI for the multi-line channel pool (backend:
 * ADR-019 / VYV-K2.1, `internal/channels`).
 *
 * Deliberately catalogue-driven: the provider picker and every credential /
 * config field render from `GET /channels/providers` — the adapter's own
 * `requiredSecrets` / `requiredConfig`. Nothing here names a provider, so
 * registering a new adapter in the backend needs zero changes to this page.
 *
 * Corrected in 138.8. The form read those two keys PER PROVIDER, and made the
 * phone number unconditionally required. That was a latent defect of this
 * ticket: it silently assumed one provider serves one channel with one BYOK
 * shape and always has a phone number. It does not — the same Meta app serves
 * WhatsApp, Messenger and Instagram DM, and the latter two have no number at
 * all. The fields now render from `requirementsFor(provider, channelType)`,
 * which is still a catalogue read and still names no provider and no channel.
 *
 * Read is open to any authenticated tenant user (matching the backend's
 * unguarded GET routes); every write control is only MOUNTED for
 * TENANT_ADMIN / PLATFORM_ADMIN, mirroring the `auth.RequireRole` boundary on
 * the POST routes. Non-admins get no disabled buttons — they get no buttons.
 */
export function ChannelLinesPage() {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN';

  const [providers, setProviders] = useState<ChannelProvider[]>([]);
  const [lines, setLines] = useState<ChannelLine[]>([]);
  // 138.3 AC4 — what the PROVIDER last reported this tenant's accounts may
  // hold. Shown, never enforced: see `load` and the hint block below.
  const [capabilities, setCapabilities] = useState<ChannelAccountCapability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Direct (API-key) onboarding form drafts. Secret drafts live only here and
  // are cleared the moment a create/rotate succeeds — they are never echoed
  // back from the server and never logged.
  const [selectedProvider, setSelectedProvider] = useState('');
  const [channelType, setChannelType] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [providerLineId, setProviderLineId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [configDraft, setConfigDraft] = useState<Record<string, string>>({});
  const [secretDraft, setSecretDraft] = useState<Record<string, string>>({});

  // Rotate drafts, keyed by line id.
  const [rotatingLine, setRotatingLine] = useState<string | null>(null);
  const [rotateDraft, setRotateDraft] = useState<Record<string, string>>({});

  // Which provider/channel popup is open, so only that control shows progress.
  const [connecting, setConnecting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cat, list] = await Promise.all([getChannelProviders(), listChannelLines()]);
      setProviders(cat);
      setLines(list);
    } catch {
      setError(t('channelLines.error_load'));
    } finally {
      setLoading(false);
    }
    // The reported caps are ADVISORY (AC4). They are fetched separately and
    // their failure is swallowed on purpose: a page that could not read an
    // optional hint must still let an admin onboard a line, and a backend that
    // predates 138.3 has no such endpoint at all.
    try {
      setCapabilities(await listChannelCapabilities());
    } catch {
      setCapabilities([]);
    }
  }, [t]);

  /**
   * The sentence for a refusal.
   *
   * 138.3 AC3 — the backend answers a STABLE code, and the code is what gets
   * translated. `detail` is a diagnostic string in English fit for a support
   * ticket, so it is only used when the code is unknown to this build (an older
   * page against a newer backend), and the generic key only when there is
   * neither.
   */
  const describeError = useCallback(
    (err: unknown, fallbackKey: string) => {
      const code = channelErrorCode(err);
      if (code) return t(`channelLines.code.${code}`);
      return extractDetail(err) ?? t(fallbackKey);
    },
    [t],
  );

  // Mount-only: `load` closes over `t`, whose identity changes when i18n
  // finishes initialising. Depending on it here would refetch the whole page
  // on that unrelated change (same reason ExternalProvidersAdminPage pins its
  // initial load to mount).
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerByKey = useMemo(
    () => new Map(providers.map((p) => [p.provider, p])),
    [providers],
  );

  const activeProvider = selectedProvider ? providerByKey.get(selectedProvider) : undefined;

  // Every (provider, channel) pair whose provider runs its own onboarding popup
  // for that channel. A catalogue read — no provider name and no channel name
  // appears in the computation or in what it renders.
  const embeddedOffers = useMemo(
    () =>
      providers.flatMap((provider) =>
        provider.channels
          .filter((channel) => requirementsFor(provider, channel).embeddedSignup === true)
          .map((channel) => ({ provider, channel })),
      ),
    [providers],
  );

  // The BYOK shape of the SELECTED channel, not of the provider as a whole.
  const activeRequirements = useMemo(
    () => (activeProvider ? requirementsFor(activeProvider, channelType) : undefined),
    [activeProvider, channelType],
  );

  // Switching channel switches the whole shape of the form: different config
  // keys, and a phone number that may not apply at all. Carrying drafts across
  // would post a waba_id to a Messenger line, or a number to a line that must
  // not have one (the backend rejects both with a 400).
  function pickChannelType(next: string) {
    setChannelType(next);
    setConfigDraft({});
    setPhoneNumber('');
  }

  function pickProvider(providerKey: string) {
    setSelectedProvider(providerKey);
    setConfigDraft({});
    setSecretDraft({});
    setPhoneNumber('');
    const provider = providerByKey.get(providerKey);
    setChannelType(provider?.channels[0] ?? '');
  }

  function resetCreateForm() {
    setSelectedProvider('');
    setChannelType('');
    setPhoneNumber('');
    setProviderLineId('');
    setDisplayName('');
    setIsDefault(false);
    setConfigDraft({});
    setSecretDraft({});
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!activeProvider) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await createChannelLine({
        channelType,
        provider: activeProvider.provider,
        phoneNumber,
        providerLineId: providerLineId || undefined,
        displayName: displayName || activeProvider.displayName,
        config: { ...configDraft },
        secrets: { ...secretDraft },
        isDefault,
      });
      resetCreateForm();
      setNotice(t('channelLines.created'));
      await load();
    } catch (err) {
      setError(describeError(err, 'channelLines.error_create'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Ticket 138.2 — onboarding through the provider's OWN popup.
   *
   * Note what is absent: no field, no prompt, no state for a WABA id, a
   * phone_number_id or a phone number. Those are facts the provider reveals
   * during the popup; the admin picks a provider and consents. Note also that
   * the completion call carries no tenant — the backend takes tenant and line
   * identity exclusively from the state it signed in the first leg, so nothing
   * this component sends could redirect a line to another tenant.
   *
   * The browser never sees an access token: the popup yields a short-lived
   * CODE, and the exchange happens server-side inside the completion request.
   */
  async function handleEmbeddedConnect(provider: ChannelProvider, channelType: ChannelType) {
    const key = `${provider.provider}:${channelType}`;
    setConnecting(key);
    setError(null);
    setNotice(null);
    try {
      const start = await startEmbeddedSignup({ provider: provider.provider, channelType });
      const outcome = await launchEmbeddedSignup(start.launch);
      await completeEmbeddedSignup({
        state: start.state,
        code: outcome.code,
        wabaId: outcome.wabaId,
        phoneNumberId: outcome.phoneNumberId,
      });
      setNotice(t('channelLines.embedded_connected'));
      await load();
    } catch (err) {
      // Closing the popup is a decision, not a failure — saying "error" there
      // would train admins to ignore the error banner.
      if (err instanceof EmbeddedSignupCancelled) {
        setNotice(t('channelLines.embedded_cancelled'));
        return;
      }
      // AC6 makes a failed ladder leave a REAL half-registered row rather than
      // rolling back, so the list is stale exactly when it failed. Without this
      // reload the admin sees an error and no line, retries, and collides with
      // the row they were never shown.
      //
      // BEFORE setError, not after: load() opens with setError(null), so
      // reloading second would silently wipe the very banner this handler exists
      // to raise. A load() failure of its own is overwritten here on purpose —
      // the ladder's refusal is the sentence the admin needs.
      await load();
      setError(describeError(err, 'channelLines.error_embedded'));
    } finally {
      setConnecting(null);
    }
  }

  function openRotate(line: ChannelLine) {
    setRotatingLine(line.id);
    setRotateDraft({});
  }

  async function handleRotate(e: FormEvent, line: ChannelLine) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await rotateChannelLine(line.id, { secrets: { ...rotateDraft } });
      setRotateDraft({});
      setRotatingLine(null);
      setNotice(t('channelLines.rotated'));
      // Fold the server's own view of the line back into the list so the
      // status/reason update without a page reload even before the refetch.
      setLines((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      await load();
    } catch (err) {
      setError(describeError(err, 'channelLines.error_rotate'));
    } finally {
      setBusy(false);
    }
  }

  async function runLineAction(
    action: () => Promise<void>,
    successKey: string,
    errorKey: string,
  ) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(t(successKey));
      // disable/disconnect/default answer 204 with no body, so the refreshed
      // list IS the new state — refetched here, no page reload.
      await load();
    } catch (err) {
      setError(describeError(err, errorKey));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Radio className="h-5 w-5 text-primary" />
          {t('channelLines.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('channelLines.subtitle')}</p>
      </div>

      {error && (
        <div
          data-testid="channel-lines-error"
          className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300"
        >
          {error}
        </div>
      )}
      {notice && (
        <div
          data-testid="channel-lines-notice"
          className="rounded-md border border-emerald-800 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-300"
        >
          {notice}
        </div>
      )}

      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}

      {/* ---------------------------------------------------------------- */}
      {/*  Registered lines                                                 */}
      {/* ---------------------------------------------------------------- */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('channelLines.lines_title')}
        </h2>

        {!loading && lines.length === 0 && (
          <div
            data-testid="channel-lines-empty"
            className="rounded-xl border bg-card px-6 py-10 text-center text-sm text-muted-foreground"
          >
            {t('channelLines.empty_lines')}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {lines.map((line) => {
            const provider = providerByKey.get(line.provider);
            return (
              <Card key={line.id} data-testid={`channel-line-${line.id}`} className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">
                      {line.displayName || line.phoneNumber || line.providerLineId}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {provider?.displayName ?? line.provider} &middot;{' '}
                      {channelLabel(t, line.channelType)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={line.status} />
                    {line.isDefault && (
                      <span
                        data-testid={`channel-line-default-badge-${line.id}`}
                        className="flex items-center gap-1 rounded-full bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-200"
                      >
                        <Star className="h-3 w-3" />
                        {t('channelLines.default_badge')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1 text-sm">
                  {/*
                    A line on a channel without a phone number shows what DOES
                    address it, rather than an empty field labelled "phone".
                  */}
                  {line.phoneNumber ? (
                    <div>
                      <span className="text-muted-foreground">
                        {t('channelLines.phone_number')}:
                      </span>{' '}
                      <code className="text-xs">{line.phoneNumber}</code>
                    </div>
                  ) : (
                    <div>
                      <span className="text-muted-foreground">
                        {t('channelLines.provider_line_id')}:
                      </span>{' '}
                      <code className="text-xs">{line.providerLineId || '—'}</code>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">{t('channelLines.origin')}:</span>{' '}
                    <span className="text-xs">{line.origin}</span>
                  </div>
                  {/*
                    138.3 AC6 — a half-registered line keeps its reason, and the
                    reason is READABLE. The backend records `<code>: <detail>`;
                    the code is translated and the raw string stays in `title`
                    for a support ticket. A reason with no code Kairos knows
                    (any pre-138.3 free text) is shown verbatim rather than
                    dropped.
                  */}
                  {line.statusReason && (
                    <p
                      data-testid={`channel-line-reason-${line.id}`}
                      title={line.statusReason}
                      className="text-xs text-amber-400"
                    >
                      {reasonText(t, line.statusReason)}
                    </p>
                  )}
                </div>

                {/*
                  Non-secret config only. The backend's LineView carries no
                  credential material at all (secrets_enc is unreachable from
                  it), and the keys shown are exactly the adapter's declared
                  requiredConfig — never a secret key.
                */}
                {provider && requirementsFor(provider, line.channelType).requiredConfig.length > 0 && (
                  <dl
                    data-testid={`channel-line-config-${line.id}`}
                    className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs"
                  >
                    {requirementsFor(provider, line.channelType).requiredConfig.map((key) => (
                      <div key={key} className="contents">
                        <dt className="text-muted-foreground">{key}</dt>
                        <dd className="truncate">{formatConfigValue(line.config?.[key])}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {isAdmin && (
                  <div className="flex flex-wrap gap-2 border-t border-sidebar-border pt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`channel-line-rotate-${line.id}`}
                      disabled={busy}
                      onClick={() => openRotate(line)}
                    >
                      {t('channelLines.rotate')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`channel-line-disable-${line.id}`}
                      disabled={busy}
                      onClick={() =>
                        void runLineAction(
                          () => disableChannelLine(line.id),
                          'channelLines.disabled',
                          'channelLines.error_disable',
                        )
                      }
                    >
                      {t('channelLines.disable')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`channel-line-disconnect-${line.id}`}
                      disabled={busy}
                      onClick={() =>
                        void runLineAction(
                          () => disconnectChannelLine(line.id),
                          'channelLines.disconnected',
                          'channelLines.error_disconnect',
                        )
                      }
                    >
                      {t('channelLines.disconnect')}
                    </Button>
                    {!line.isDefault && (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`channel-line-default-${line.id}`}
                        disabled={busy}
                        onClick={() =>
                          void runLineAction(
                            () => setDefaultChannelLine(line.id, line.channelType),
                            'channelLines.default_set',
                            'channelLines.error_default',
                          )
                        }
                      >
                        {t('channelLines.make_default')}
                      </Button>
                    )}
                  </div>
                )}

                {isAdmin && rotatingLine === line.id && provider && (
                  <form
                    data-testid={`channel-line-rotate-form-${line.id}`}
                    onSubmit={(e) => void handleRotate(e, line)}
                    className="space-y-2 border-t border-sidebar-border pt-3"
                  >
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t('channelLines.rotate_title')}
                    </p>
                    {requirementsFor(provider, line.channelType).requiredSecrets.map((key) => (
                      <div key={key} className="space-y-1">
                        <label className="text-xs font-medium" htmlFor={`rotate-${line.id}-${key}`}>
                          {key}
                        </label>
                        <Input
                          id={`rotate-${line.id}-${key}`}
                          type="password"
                          required
                          autoComplete="off"
                          value={rotateDraft[key] ?? ''}
                          onChange={(e) =>
                            setRotateDraft((prev) => ({ ...prev, [key]: e.target.value }))
                          }
                        />
                      </div>
                    ))}
                    <div className="flex justify-end gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => {
                          setRotatingLine(null);
                          setRotateDraft({});
                        }}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        type="submit"
                        data-testid={`channel-line-rotate-submit-${line.id}`}
                        disabled={busy}
                      >
                        {busy ? t('common.saving') : t('channelLines.rotate_submit')}
                      </Button>
                    </div>
                  </form>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      {/* ---------------------------------------------------------------- */}
      {/*  Provider catalogue + direct onboarding                           */}
      {/* ---------------------------------------------------------------- */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('channelLines.catalogue_title')}
        </h2>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {providers.map((provider) => (
            <Card
              key={provider.provider}
              data-testid={`channel-provider-${provider.provider}`}
              className="space-y-1"
            >
              <h3 className="text-sm font-semibold">{provider.displayName}</h3>
              {/*
                One line per channel, naming the channel and its own
                requirements: a provider serving several channels has several
                BYOK shapes, and the single collapsed list this replaced said
                something untrue about every channel but the first.
              */}
              {provider.channels.map((ch) => {
                const req = requirementsFor(provider, ch);
                return (
                  <p key={ch} className="text-xs text-muted-foreground">
                    <span className="font-medium">{channelLabel(t, ch)}</span>{' '}
                    {t('channelLines.requires', {
                      config: req.requiredConfig.join(', '),
                      secrets: req.requiredSecrets.join(', '),
                    })}
                  </p>
                );
              })}
            </Card>
          ))}
        </div>

        {!loading && providers.length === 0 && (
          <div
            data-testid="channel-providers-empty"
            className="rounded-xl border bg-card px-6 py-10 text-center text-sm text-muted-foreground"
          >
            {t('channelLines.empty_providers')}
          </div>
        )}

        {/*
          Provider-run onboarding (138.2). Rendered from the catalogue's
          per-channel `embeddedSignup` flag, so this block names no provider and
          no channel: a backend that registers another adapter with a popup gets
          a control here for free, and a deployment missing the platform app
          credentials gets none — which is exactly what a blank config id means.
        */}
        {isAdmin && embeddedOffers.length > 0 && (
          <Card data-testid="channel-embedded-signup" className="space-y-3">
            <h3 className="font-semibold">{t('channelLines.embedded_title')}</h3>
            <p className="text-xs text-muted-foreground">{t('channelLines.embedded_hint')}</p>
            <div className="flex flex-wrap gap-2">
              {embeddedOffers.map(({ provider, channel }) => {
                const key = `${provider.provider}:${channel}`;
                return (
                  <Button
                    key={key}
                    size="sm"
                    data-testid={`channel-embedded-connect-${provider.provider}-${channel}`}
                    disabled={busy || connecting !== null}
                    onClick={() => void handleEmbeddedConnect(provider, channel)}
                  >
                    {connecting === key
                      ? t('channelLines.embedded_connecting')
                      : t('channelLines.embedded_connect', { provider: provider.displayName })}
                  </Button>
                );
              })}
            </div>
          </Card>
        )}

        {isAdmin && (
          <Card data-testid="channel-line-create" className="space-y-3">
            <h3 className="font-semibold">{t('channelLines.onboard_title')}</h3>
            <p
              data-testid="channel-lines-cap-hint"
              className="rounded-md border border-sidebar-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
            >
              {t('channelLines.number_cap_hint')}
            </p>

            {/*
              138.3 AC4 — the cap the PROVIDER reported for each account, next
              to the form it concerns. Rendered as information and nothing else:
              the submit button below is never disabled by it, and no branch
              here refuses a submit. The provider is the only authority on
              whether one more number is allowed, and this copy is a fact about
              the past — refusing on it would cost the tenant a line the
              provider would have granted.
            */}
            {capabilities.length > 0 && (
              <ul data-testid="channel-lines-reported-caps" className="space-y-1">
                {capabilities.map((cap) => (
                  <li
                    key={`${cap.provider}:${cap.accountRef}`}
                    data-testid={`channel-lines-reported-cap-${cap.provider}-${cap.accountRef}`}
                    className="rounded-md border border-sidebar-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"
                  >
                    {t('channelLines.reported_cap', {
                      account: cap.accountRef,
                      max: cap.maxPhoneNumbers,
                      provider: providerByKey.get(cap.provider)?.displayName ?? cap.provider,
                    })}
                  </li>
                ))}
              </ul>
            )}

            <form onSubmit={(e) => void handleCreate(e)} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="channel-line-provider">
                  {t('channelLines.provider')}
                </label>
                <Select
                  id="channel-line-provider"
                  data-testid="channel-line-provider-select"
                  required
                  value={selectedProvider}
                  onChange={(e) => pickProvider(e.target.value)}
                >
                  <option value="">{t('channelLines.provider_placeholder')}</option>
                  {providers.map((provider) => (
                    <option key={provider.provider} value={provider.provider}>
                      {provider.displayName}
                    </option>
                  ))}
                </Select>
              </div>

              {activeProvider && (
                <div data-testid={`channel-line-form-${activeProvider.provider}`} className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="channel-line-channel-type">
                      {t('channelLines.channel_type')}
                    </label>
                    <Select
                      id="channel-line-channel-type"
                      data-testid="channel-line-channel-type-select"
                      required
                      value={channelType}
                      onChange={(e) => pickChannelType(e.target.value)}
                    >
                      {activeProvider.channels.map((ch) => (
                        <option key={ch} value={ch}>
                          {channelLabel(t, ch)}
                        </option>
                      ))}
                    </Select>
                  </div>

                  {/*
                    Rendered only for a channel the catalogue declares as
                    phone-addressed. A Messenger page or an Instagram account
                    has no number, and sending one is a 400 — so the field is
                    absent rather than optional-and-ignored.
                  */}
                  {activeRequirements?.phoneNumber && (
                    <div className="space-y-1">
                      <label className="text-xs font-medium" htmlFor="channel-line-phone">
                        {t('channelLines.phone_number')}
                      </label>
                      <Input
                        id="channel-line-phone"
                        data-testid="channel-line-phone-input"
                        required
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+34600000000"
                      />
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="channel-line-display-name">
                      {t('channelLines.display_name')}
                    </label>
                    <Input
                      id="channel-line-display-name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder={activeProvider.displayName}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="channel-line-provider-line-id">
                      {t('channelLines.provider_line_id')}
                    </label>
                    <Input
                      id="channel-line-provider-line-id"
                      data-testid="channel-line-provider-line-id-input"
                      /* It ADDRESSES the line when there is no phone number. */
                      required={activeRequirements ? !activeRequirements.phoneNumber : false}
                      value={providerLineId}
                      onChange={(e) => setProviderLineId(e.target.value)}
                    />
                  </div>

                  {/* Config fields: EXACTLY this CHANNEL's requiredConfig. */}
                  {(activeRequirements?.requiredConfig ?? []).map((key) => (
                    <div key={key} className="space-y-1">
                      <label className="text-xs font-medium" htmlFor={`config-${key}`}>
                        {key}
                      </label>
                      <Input
                        id={`config-${key}`}
                        data-testid={`channel-line-config-input-${key}`}
                        required
                        value={configDraft[key] ?? ''}
                        onChange={(e) =>
                          setConfigDraft((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    </div>
                  ))}

                  {/* Secret fields: EXACTLY this CHANNEL's requiredSecrets. */}
                  {(activeRequirements?.requiredSecrets ?? []).map((key) => (
                    <div key={key} className="space-y-1">
                      <label className="text-xs font-medium" htmlFor={`secret-${key}`}>
                        {key}
                      </label>
                      <Input
                        id={`secret-${key}`}
                        data-testid={`channel-line-secret-input-${key}`}
                        type="password"
                        required
                        autoComplete="off"
                        value={secretDraft[key] ?? ''}
                        onChange={(e) =>
                          setSecretDraft((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                      />
                    </div>
                  ))}

                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      data-testid="channel-line-default-checkbox"
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                    />
                    {t('channelLines.set_as_default')}
                  </label>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      size="sm"
                      data-testid="channel-line-create-submit"
                      disabled={busy}
                    >
                      {busy ? t('common.saving') : t('channelLines.create')}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </Card>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: LineStatus }) {
  const { t } = useTranslation();
  if (status === 'ACTIVE') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200">
        <CheckCircle2 className="h-3 w-3" />
        {t('channelLines.status_active')}
      </span>
    );
  }
  if (status === 'DISABLED') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
        <PauseCircle className="h-3 w-3" />
        {t('channelLines.status_disabled')}
      </span>
    );
  }
  if (status === 'ERROR') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-red-900/40 px-2 py-0.5 text-xs font-medium text-red-200">
        <AlertCircle className="h-3 w-3" />
        {t('channelLines.status_error')}
      </span>
    );
  }
  /*
    138.3 AC1/AC6 — a line whose provider registration has not finished. It
    reads as unfinished, NOT as connected: the whole point of the state is that
    such a line looks connected and is mute. It is deliberately not folded into
    the ERROR badge either, because nothing failed.
  */
  if (status === 'PENDING_REGISTRATION') {
    return (
      <span className="flex items-center gap-1 rounded-full bg-sky-900/40 px-2 py-0.5 text-xs font-medium text-sky-200">
        <Clock className="h-3 w-3" />
        {t('channelLines.status_pending_registration')}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 rounded-full bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-200">
      <XCircle className="h-3 w-3" />
      {t('channelLines.status_disconnected')}
    </span>
  );
}

/**
 * The readable form of a line's `status_reason` (138.3 AC6).
 *
 * Never the raw string when Kairos knows the code: the detail after the colon
 * is a provider diagnostic in English, and ADR-033 puts the user-facing
 * sentence in the translation catalogue. Never empty either — an unknown reason
 * is passed through, because losing it would hide the only clue there is.
 */
function reasonText(t: (key: string) => string, reason: string): string {
  const code = statusReasonCode(reason);
  return code ? t(`channelLines.code.${code}`) : reason;
}

/**
 * 138.8 finding F5 — a channel identifier is a WIRE value.
 *
 * `whatsapp`, `facebook_messenger` and `instagram_dm` are the strings the
 * backend keys its adapter registry on; they were being rendered straight into
 * the page in three places, so a Spanish-speaking admin read
 * `facebook_messenger` where the product promises "Facebook Messenger".
 *
 * The `defaultValue` is not decoration. `channels.ChannelType` is deliberately
 * ADDITIVE — a new channel needs no migration and no CHECK — so this UI must
 * keep working for a channel the locale files have never heard of. It falls
 * back to the raw identifier, which is ugly but true, rather than to the
 * missing-key string, which would render `channelLines.channel.telegram` to a
 * user.
 */
function channelLabel(
  t: (key: string, opts?: { defaultValue: string }) => string,
  channel: string,
): string {
  return t(`channelLines.channel.${channel}`, { defaultValue: channel });
}

function formatConfigValue(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '—';
}

function extractDetail(err: unknown): string | null {
  if (isAxiosError(err)) {
    return (err.response?.data as { detail?: string } | undefined)?.detail ?? null;
  }
  return null;
}
